import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { applyServerErrors } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input'
import { PasswordInput } from '@/components/ui/PasswordInput';
import { authApi } from '../api';
import { loginSchema, type LoginValues } from '../schemas';
import { AuthLayout } from '../components/AuthLayout';
import { DemoAccounts } from '../components/DemoAccounts';
import type { DemoAccount } from '../demoAccounts';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  const setToken = useAuthStore((s) => s.setToken);
  const setSession = useAuthStore((s) => s.setSession);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const login = useMutation({
    mutationFn: (values: LoginValues) => authApi.login(values),
    onSuccess: (session) => {
      setToken(session.token);
      setSession(session);

      // Seed the session query so ProtectedRoute does not immediately
      // refetch what we already hold.
      queryClient.setQueryData(['session'], session);

      // Only ever a same-origin path. Taking an absolute URL from the query
      // string would be an open redirect.
      const next = params.get('next');
      const destination = next && next.startsWith('/') ? next : '/dashboard';

      navigate(destination, { replace: true });
    },
    onError: (error) => {
      if (!applyServerErrors(error, form.setError as never)) {
        toast.error(error instanceof Error ? error.message : 'Could not sign in.');
      }
    },
  });

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to open your till."
      footer={
        <p className="text-sm text-ink-secondary">
          New to Voltra?{' '}
          <Link
            to="/register"
            className="font-semibold text-ink underline underline-offset-4 hover:text-brand"
          >
            Create a workspace
          </Link>
        </p>
      }
    >
      <form
        onSubmit={form.handleSubmit((values) => login.mutate(values))}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label="Email" htmlFor="email" error={form.formState.errors.email?.message} required>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            autoFocus
            aria-invalid={!!form.formState.errors.email}
            {...form.register('email')}
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          error={form.formState.errors.password?.message}
          required
        >
          <PasswordInput
            id="password"
            autoComplete="current-password"
            aria-invalid={!!form.formState.errors.password}
            {...form.register('password')}
          />
        </Field>

        <div className="-mt-1 flex justify-end">
          <Link to="/forgot-password" className="text-sm text-ink-secondary hover:text-brand">
            Forgot password?
          </Link>
        </div>

        <Button type="submit" size="lg" loading={login.isPending} className="mt-1 w-full">
          Sign in
        </Button>
      </form>

      {/* Fills the form as well as submitting it, so the credentials are
          visible rather than magic — someone demoing usually wants to say
          what they signed in as. */}
      <DemoAccounts
        disabled={login.isPending}
        onPick={(account: DemoAccount) => {
          form.setValue('email', account.email);
          form.setValue('password', account.password);
          login.mutate({ email: account.email, password: account.password });
        }}
      />
    </AuthLayout>
  );
}
